import React, { FC, useEffect, useMemo, useState, useRef } from "react";
import { Typography, Spin } from "antd";
import styles from "./create-proposal.module.css";
import { SearchFlowState } from "~/containers/create-proposal-container";

const {
  ["streaming-summary-container"]: containerClassName,
  ["streaming-summary-loading"]: loadingClassName,
  ["carousel-wrapper"]: carouselWrapperClassName,
  ["carousel-text"]: carouselTextClassName,
} = styles;

interface SummaryStreamingState {
  sessionId: string | null;
  chunks: string[];
  fullSummary: string | null;
  isComplete: boolean;
  error: string | null;
  streamingActive: boolean;
}

interface StreamingSummarySectionProps {
  summaryStreamingState: SummaryStreamingState;
  searchFlowState: SearchFlowState;
}

export const StreamingSummarySection: FC<StreamingSummarySectionProps> = ({
  summaryStreamingState,
  searchFlowState,
}) => {
  // Show during summary generation or when waiting for search results
  if (
    searchFlowState !== "generating_summary" &&
    searchFlowState !== "waiting_for_search_results_with_summary"
  ) {
    return null;
  }

  const currentText = summaryStreamingState.chunks.join("");
  const hasError = !!summaryStreamingState.error;

  const thoughts = useMemo(() => {
    return currentText.split(";").filter((thought) => thought.trim() !== "");
  }, [currentText]);

  /* ---------------------------------------------
   * Carousel scrolling logic
   * -------------------------------------------*/

  // Build the long carousel string with 20-space separators
  const SPACE_SEPARATOR = "                    "; // 20 spaces
  const carouselString = useMemo(() => {
    if (thoughts.length === 0) return "";
    // Ensure there is always a trailing separator so the last thought slides out nicely
    return thoughts.join(SPACE_SEPARATOR) + SPACE_SEPARATOR;
  }, [thoughts]);

  /* ---------------------------------------------
   * CSS-driven smooth scroll
   * -------------------------------------------*/

  // Duplicate string to allow seamless loop
  const displayString = useMemo(() => {
    if (carouselString.length === 0) return "";
    return carouselString + carouselString;
  }, [carouselString]);

  // Measure width of the first half to set scroll distance & duration
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [scrollDistance, setScrollDistance] = useState(0);
  const [animationDuration, setAnimationDuration] = useState(0);

  useEffect(() => {
    if (!textRef.current) return;
    // Width of the first half (original carouselString)
    const halfWidth = textRef.current.offsetWidth / 2;
    setScrollDistance(halfWidth);
    const SPEED_PIXELS_PER_SEC = 240;
    setAnimationDuration(halfWidth / SPEED_PIXELS_PER_SEC);
  }, [carouselString]);

  const carouselSpanStyle = useMemo<React.CSSProperties>(
    () => ({
      animationDuration: `${animationDuration}s`,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - CSS custom property not in type definitions
      "--scroll-distance": `${scrollDistance}px`,
    }),
    [animationDuration, scrollDistance]
  );

  return (
    <div className={containerClassName}>
      <div style={{ marginBottom: "20px" }}>
        {/* Always show "Thinking about your matter" spinner */}
        <div className={loadingClassName}>
          <Spin size="large" />
          <Typography.Text type="secondary" style={{ marginTop: "12px", display: "block" }}>
            Thinking about your matter...
          </Typography.Text>
        </div>

        {/* Show error, carousel, or nothing based on state */}
        {hasError ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Typography.Text type="danger">
              Error generating summary: {summaryStreamingState.error}
            </Typography.Text>
          </div>
        ) : thoughts.length > 2 ? (
          <div style={{ marginTop: "20px" }}>
            <div className={carouselWrapperClassName}>
              <span className={carouselTextClassName} ref={textRef} style={carouselSpanStyle}>
                {displayString}
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
